class SizeGuide extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.source = this.querySelector('[data-size-guide-source]');
    this.interface = this.querySelector('[data-size-guide-interface]');
    this.measurementsTarget = this.querySelector('[data-size-guide-measurements]');
    this.conversionTarget = this.querySelector('[data-size-guide-conversion]');
    this.tabs = Array.from(this.querySelectorAll('[data-size-guide-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-size-guide-panel]'));
    this.unitButtons = Array.from(this.querySelectorAll('[data-size-guide-unit]'));

    const data = this.readSourceTables();
    if (!data.conversion || (!data.in && !data.cm)) return;

    this.renderConversionTable(data.conversion);
    this.measurementTables = data;
    this.bindEvents();
    this.dataset.activePanel =
      this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true')?.dataset.sizeGuideTab || 'measurements';
    this.setUnit(this.getPreferredUnit(data), false);
    this.setSupportNote();

    this.source.hidden = true;
    this.interface.hidden = false;
    this.classList.add('is-enhanced');
    this.initialized = true;
  }

  readSourceTables() {
    const result = { conversion: null, in: null, cm: null };

    this.source.querySelectorAll('table').forEach((table) => {
      const matrix = Array.from(table.rows)
        .map((row) => Array.from(row.cells).map((cell) => cell.textContent.replace(/\s+/g, ' ').trim()))
        .filter((row) => row.some(Boolean));

      if (matrix.length < 2) return;

      const headers = matrix[0].map((value) => value.toLowerCase());
      const context = this.getTableContext(table).toLowerCase();
      const parsed = { headers: matrix[0], rows: matrix.slice(1) };

      if (headers.includes('uk') && headers.includes('us')) {
        result.conversion = parsed;
      } else if (headers.includes('bust') && headers.includes('waist') && headers.some((value) => value === 'hip' || value === 'hips')) {
        if (context.includes('cm')) result.cm = parsed;
        else if (context.includes('inch') || context.includes(' in')) result.in = parsed;
      }
    });

    return result;
  }

  getTableContext(table) {
    const text = [];
    let current = table.previousElementSibling;

    while (current && current.tagName !== 'TABLE') {
      text.unshift(current.textContent);
      if (/^H[1-6]$/.test(current.tagName)) break;
      current = current.previousElementSibling;
    }

    return text.join(' ');
  }

  getPreferredUnit(data) {
    let storedUnit;

    try {
      storedUnit = sessionStorage.getItem(this.dataset.storageKey);
    } catch (error) {
      storedUnit = null;
    }

    if (storedUnit && data[storedUnit]) return storedUnit;
    if (data[this.dataset.defaultUnit]) return this.dataset.defaultUnit;
    return data.in ? 'in' : 'cm';
  }

  bindEvents() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.activateTab(tab.dataset.sizeGuideTab));
      tab.addEventListener('keydown', (event) => this.onTabKeydown(event));
    });

    this.unitButtons.forEach((button) => {
      button.addEventListener('click', () => this.setUnit(button.dataset.sizeGuideUnit));
    });
  }

  activateTab(name, moveFocus = false) {
    this.dataset.activePanel = name;

    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.sizeGuideTab === name;
      tab.setAttribute('aria-selected', isActive.toString());
      tab.tabIndex = isActive ? 0 : -1;
      if (isActive && moveFocus) tab.focus();
    });

    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.sizeGuidePanel !== name;
    });
  }

  onTabKeydown(event) {
    const supportedKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!supportedKeys.includes(event.key)) return;

    event.preventDefault();
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    let nextIndex;

    if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = this.tabs.length - 1;
    else {
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      nextIndex = (currentIndex + direction + this.tabs.length) % this.tabs.length;
    }

    this.activateTab(this.tabs[nextIndex].dataset.sizeGuideTab, true);
  }

  setUnit(unit, store = true) {
    const table = this.measurementTables[unit];
    if (!table) return;

    this.unitButtons.forEach((button) => {
      button.setAttribute('aria-pressed', (button.dataset.sizeGuideUnit === unit).toString());
      button.disabled = !this.measurementTables[button.dataset.sizeGuideUnit];
    });

    this.renderMeasurementTables(table, unit);

    if (store) {
      try {
        sessionStorage.setItem(this.dataset.storageKey, unit);
      } catch (error) {
        // The guide still works when storage is unavailable.
      }
    }
  }

  renderMeasurementTables(data, unit) {
    this.measurementsTarget.replaceChildren(
      this.buildDesktopMeasurementTable(data, unit),
      this.buildMobileMeasurementTable(data, unit)
    );
  }

  buildDesktopMeasurementTable(data, unit) {
    const table = this.createTable(`Body measurements in ${this.unitName(unit)}`, 'size-guide__table size-guide__table--desktop');
    const thead = table.createTHead();
    const headingRow = thead.insertRow();
    this.addCell(headingRow, 'Measurement', 'th', 'col');

    data.rows.forEach((row) => this.addCell(headingRow, row[0], 'th', 'col'));

    const tbody = table.createTBody();
    data.headers.slice(1).forEach((measurement, measurementIndex) => {
      const row = tbody.insertRow();
      this.addCell(row, `${measurement} (${unit.toUpperCase()})`, 'th', 'row');
      data.rows.forEach((sizeRow) => this.addCell(row, sizeRow[measurementIndex + 1]));
    });

    return table;
  }

  buildMobileMeasurementTable(data, unit) {
    const table = this.createTable(`Body measurements in ${this.unitName(unit)}`, 'size-guide__table size-guide__table--mobile');
    const thead = table.createTHead();
    const headingRow = thead.insertRow();

    data.headers.forEach((header, index) => {
      const label = index === 0 ? header : `${header} (${unit.toUpperCase()})`;
      this.addCell(headingRow, label, 'th', 'col');
    });

    const tbody = table.createTBody();
    data.rows.forEach((values) => {
      const row = tbody.insertRow();
      values.forEach((value, index) => this.addCell(row, value, index === 0 ? 'th' : 'td', index === 0 ? 'row' : null));
    });

    return table;
  }

  renderConversionTable(data) {
    const table = this.createTable('International size conversion', 'size-guide__table size-guide__table--conversion');
    const thead = table.createTHead();
    const headingRow = thead.insertRow();

    data.headers.forEach((header) => this.addCell(headingRow, header, 'th', 'col'));

    const tbody = table.createTBody();
    data.rows.forEach((values) => {
      const row = tbody.insertRow();
      values.forEach((value, index) => this.addCell(row, value, index === 0 ? 'th' : 'td', index === 0 ? 'row' : null));
    });

    this.conversionTarget.replaceChildren(table);
  }

  createTable(captionText, className) {
    const table = document.createElement('table');
    const caption = table.createCaption();
    caption.textContent = captionText;
    caption.className = 'visually-hidden';
    table.className = className;
    return table;
  }

  addCell(row, text, tagName = 'td', scope = null) {
    const cell = document.createElement(tagName);
    cell.textContent = text;
    if (scope) cell.scope = scope;
    row.appendChild(cell);
  }

  unitName(unit) {
    return unit === 'cm' ? 'centimetres' : 'inches';
  }

  setSupportNote() {
    const support = this.querySelector('[data-size-guide-support]');
    const sourceParagraph = Array.from(this.source.querySelectorAll('p')).find((paragraph) => {
      const text = paragraph.textContent.trim();
      return text && (/support|question|contact|hello@/i.test(text));
    });

    if (!support || !sourceParagraph) return;
    const supportText = sourceParagraph.textContent.trim();
    const emailMatch = supportText.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);

    support.replaceChildren();

    if (emailMatch) {
      const email = emailMatch[0];
      const emailIndex = emailMatch.index;
      const emailLink = document.createElement('a');

      support.append(document.createTextNode(supportText.slice(0, emailIndex)));
      emailLink.href = `mailto:${email}`;
      emailLink.textContent = email;
      support.append(emailLink);
      support.append(document.createTextNode(supportText.slice(emailIndex + email.length)));
    } else {
      support.textContent = supportText;
    }

    support.hidden = false;
  }
}

if (!customElements.get('size-guide')) {
  customElements.define('size-guide', SizeGuide);
}
